import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import CarouselHeader from '@/components/FullPageCarousel/CarouselHeader';
import { Button } from '@/components/ui/button';
import { cn, validateStep } from '@/lib/utils';
import {
  signupBasicDetailsWithEmailSchema,
  signupBasicDetailsWithGoogleSchema,
  signupOtherDetailsSchema,
} from '@/redux/auth/auth.schema';
import BasicDetails from './Carousel/BasicDetails';
import { AuthProvider, IUserSignupData } from '@/redux/auth/auth.d';
import OtherDetails from './Carousel/OtherDetails';
import { selectCurrentUser } from '@/redux/auth/auth.slice';
import {
  useCompleteUserSignupMutation,
  useGetUserQuery,
  useLogoutMutation,
  useSignupMutation,
} from '@/redux/auth/auth.api.slice';
import { setShowLoginDialog } from '@/redux/global/global.slice';

export interface IUserSignupDataWithConfirmPassword extends IUserSignupData {
  confirmPassword: string;
}

function Signup() {
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { user, isAuthenticated } = useSelector(selectCurrentUser);
  const [signup, { isLoading: isSignupLoading }] = useSignupMutation();
  const { refetch: refetchUser } = useGetUserQuery();
  const dispatch = useDispatch();

  const [logout] = useLogoutMutation();
  const [completeSignup, { isLoading: isCompleteSignupLoading }] =
    useCompleteUserSignupMutation();
  const navigate = useNavigate();

  const IS_USER_SINGUP_COMPLETE: boolean = Boolean(user?.isUserSignupComplete);
  const IS_SOCIAL_LOGIN: boolean = user?.authProvider !== AuthProvider.Email;

  const [formData, setFormData] = useState<IUserSignupDataWithConfirmPassword>({
    profilePicture: undefined,
    username: '',
    firstname: '',
    lastname: '',
    email: '',
    cncBrand: [],
    cncModel: [],
    cncVariant: [],
    password: '',
    confirmPassword: '',
    locatedAt: 'Yet to be collected',
  });

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        cncBrand: user.cncBrand,
        cncModel: user.cncModel,
        profilePicture: user.profilePicture,
      }));
    }
  }, [user, isAuthenticated]);

  const pages = [
    {
      page: (
        <BasicDetails
          formData={formData}
          errors={errors}
          setFormData={setFormData}
          user={user}
          key="basic-details"
        />
      ),
      schema: !(Boolean(user) && IS_SOCIAL_LOGIN && !IS_USER_SINGUP_COMPLETE)
        ? signupBasicDetailsWithEmailSchema
        : signupBasicDetailsWithGoogleSchema,
    },
    {
      page: (
        <OtherDetails
          formData={formData}
          errors={errors}
          setFormData={setFormData}
          key="other-details"
        />
      ),
      schema: signupOtherDetailsSchema,
    },
  ];

  const handleSignupSubmit = async () => {
    try {
      if (!(Boolean(user) && IS_SOCIAL_LOGIN && !IS_USER_SINGUP_COMPLETE)) {
        await signup({
          email: formData.email,
          password: formData.password,
          username: formData.username,
          firstname: formData.firstname,
          lastname: formData.lastname,
          cncBrand: formData.cncBrand,
          cncModel: formData.cncModel,
          cncVariant: formData.cncVariant,
          locatedAt: 'Yet to be collected',
          ...(formData.profilePicture
            ? { profilePicture: formData.profilePicture }
            : {}),
        }).unwrap();
        await refetchUser();
      } else {
        await completeSignup({
          username: formData.username,
          firstname: formData.firstname,
          lastname: formData.lastname,
          cncBrand: formData.cncBrand,
          cncModel: formData.cncModel,
          cncVariant: formData.cncVariant,
          locatedAt: 'Yet to be collected',
          ...(formData.profilePicture
            ? { profilePicture: formData.profilePicture }
            : {}),
        }).unwrap();
        await refetchUser();
      }
      toast.success('Signup successful');
      navigate('/');
    } catch (error: any) {
      toast.error('Signup Failed', {
        description: error.data.errorMessage,
      });
    }
  };

  const handleNext = async () => {
    const validationResult = await validateStep(
      currentStep,
      pages,
      formData,
      setErrors
    );
    if (validationResult.success) {
      setErrors({});
      setCurrentStep((prev) => Math.min(prev + 1, pages.length));
    } else {
      toast.error('Please fix the errors before continuing.');
      return;
    }

    if (currentStep === pages.length) {
      handleSignupSubmit();
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  if (Boolean(user) && IS_USER_SINGUP_COMPLETE) {
    navigate('/');
  }

  const handleLogin = async () => {
    await logout();
    await refetchUser();
    navigate('/');
    dispatch(setShowLoginDialog(true));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background flex-col pb-[100px]">
      <CarouselHeader disabled={isCompleteSignupLoading || isSignupLoading} />
      <div className="flex-grow flex items-center justify-center w-full">
        <div className="w-full max-w-lg space-y-8 p-3">
          <div className="flex items-center space-x-2">
            {pages.map((_, index) => (
              <Fragment key={index + 1}>
                <div
                  className={cn(
                    'w-3 h-3 rounded-full',
                    currentStep >= index + 1 ? 'bg-[#34C759]' : 'bg-muted'
                  )}
                />
                {index + 1 < pages.length && (
                  <div
                    className={cn(
                      'w-16 h-0.5',
                      currentStep > index + 1 ? 'bg-[#34C759]' : 'bg-muted'
                    )}
                  />
                )}
              </Fragment>
            ))}
          </div>
          {pages[currentStep - 1].page}
          <div className="flex justify-between mt-8 w-full gap-2">
            {currentStep > 1 && currentStep < pages.length && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="w-full rounded-lg"
              >
                Back
              </Button>
            )}
            {currentStep < pages.length ? (
              <Button className="w-full rounded-lg" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <>
                {currentStep >= 1 && currentStep <= pages.length && (
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    className="w-full rounded-lg"
                  >
                    Back
                  </Button>
                )}
                <Button
                  className="w-full rounded-lg"
                  onClick={handleNext}
                  disabled={isCompleteSignupLoading || isSignupLoading}
                >
                  Signup
                </Button>
              </>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Already have an account with filefinity,{' '}
            <Button
              variant="link"
              className="text-primary underline cursor-pointer bg-transparent border-none p-0"
              onClick={handleLogin}
            >
              login here
            </Button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Signup;
